import { darken, getLuminance, Theme } from '@mui/material';
import { DataVisualizationColor } from '@mui/material/styles/createPalette';
import { ReactElement } from 'react';

import { SemanticTileKind } from '@/components/diagrams/diagramStyle';
import { ArrowUpIcon } from '@/components/icons/ArrowUpIcon';
import { BankIcon } from '@/components/icons/BankIcon';
import { CoinsHandIcon } from '@/components/icons/CoinsHandIcon';
import { CurrencyDollarCircleIcon } from '@/components/icons/CurrencyDollarCircleIcon';
import { EyeOffIcon } from '@/components/icons/EyeOffIcon';
import { Family01Icon } from '@/components/icons/Family01Icon';
import { File02Icon } from '@/components/icons/File02Icon';
import { HeartIcon } from '@/components/icons/HeartIcon';
import { Home03Icon, IconProps } from '@/components/icons/Home03Icon';
import { User01Icon } from '@/components/icons/User01Icon';
import { User03Icon } from '@/components/icons/User03Icon';
import { CHARITABLE_ENTITY_KINDS } from '@/modules/entities/entities.constants';
import { ESTATE_CATEGORY_ICON } from '@/modules/estate/estateCategoryIcons';
import { GraphVizTileEmphasis } from '@/modules/graphViz/graphVizNodeConfig';
import {
  generateDataVisualizationPaletteFromBaseColor,
  getContrastTextForColor,
  getReadableTextColor,
} from '@/styles/themes/common';
import { COLORS } from '@/styles/tokens/colors';
import {
  EntityInEstateStatus,
  EntityKind,
  TestamentaryEntityKind,
} from '@/types/schema';

import {
  ORDERED_TILE_FONT_SIZE_VARIANTS,
  TILE_DEFAULT_FONT_SIZE_VARIANT,
  TILE_DEFAULT_WIDTH,
  TILE_WIDTH_ADJUSTMENT_DECREMENT,
  TILE_WIDTH_ADJUSTMENT_INCREMENT,
} from './constants';
import { TileFontSizeVariant, TileVariant } from './types';

function getPaletteForVariant(
  theme: Theme,
  variant: TileVariant
): Pick<DataVisualizationColor, 'main' | 'lighter' | 'ultraLight'> | undefined {
  const map: Partial<
    Record<
      TileVariant,
      Pick<DataVisualizationColor, 'main' | 'lighter' | 'ultraLight'>
    >
  > = {
    [TileVariant.Primary]: theme.palette.dataVisualizationPrimary,
    [TileVariant.Secondary]: theme.palette.dataVisualizationSecondary,
    [TileVariant.Tertiary]: theme.palette.dataVisualizationTertiary,
    [TileVariant.Destructive]: theme.palette.dataVisualizationNegative,
  };
  return map[variant] ?? map[TileVariant.Primary];
}

/**
 * Returns the shades that color a tile: the shades of `customColor`, or the
 * theme palette of `variant`.
 */
export function getTilePalette(
  theme: Theme,
  variant: TileVariant,
  customColor?: string
) {
  return customColor
    ? generateDataVisualizationPaletteFromBaseColor(customColor)
    : getPaletteForVariant(theme, variant);
}

/**
 * Returns the appropriate text color for a tile given its variant and
 * optional emphasis level.
 *
 * Legacy tiles (no emphasis set) and tiles explicitly set to Low keep the
 * pre-emphasis behavior of contrasting against `palette.lighter`. Medium/High
 * use a luminance split against the resolved body shade — if the body is dark
 * enough that white text reads better we invert; otherwise we return a
 * darkened variant of the body so text stays legible on tenant-branded colors.
 */
export function getTextColorForVariant(
  theme: Theme,
  variant: TileVariant,
  emphasis?: GraphVizTileEmphasis,
  customColor?: string
) {
  if (customColor) {
    // A custom color can be any hue and saturation, so a luminance split can
    // select a weak foreground. Compare contrast against the drawn body.
    const palette = generateDataVisualizationPaletteFromBaseColor(customColor);
    return getReadableTextColor(getTileBodyShadeForEmphasis(palette, emphasis));
  }
  const palette = getPaletteForVariant(theme, variant);
  if (!palette) return undefined;
  if (!emphasis || emphasis === GraphVizTileEmphasis.Low) {
    return getContrastTextForColor(palette.lighter);
  }
  return getTileTextColorForBody(
    getTileBodyShadeForEmphasis(palette, emphasis)
  );
}

/**
 * Picks a legible text color for a tile given its resolved body shade.
 * Luminance below 0.5 means the body is dark, so we invert to white;
 * otherwise we return a darkened variant of the body that contrasts
 * against the light body while keeping the hue.
 */
export function getTileTextColorForBody(body: string): string {
  return getLuminance(body) < 0.5 ? COLORS.PRIMITIVES.WHITE : darken(body, 0.8);
}

/**
 * Returns the palette shade used for the body background of a tile at the
 * given emphasis level. Low uses the ultraLight shade (current behavior),
 * Medium uses the lighter shade, and High uses the base brand color.
 * Text color is derived separately from this body shade via
 * `getTileTextColorForBody`, which auto-inverts when the body is dark.
 */
export function getTileBodyShadeForEmphasis(
  palette: Pick<DataVisualizationColor, 'main' | 'lighter' | 'ultraLight'>,
  emphasis?: GraphVizTileEmphasis
): string {
  if (emphasis === GraphVizTileEmphasis.High) return palette.main;
  if (emphasis === GraphVizTileEmphasis.Medium) return palette.lighter;
  return palette.ultraLight;
}

/**
 * Returns the palette shade used for the left accent stripe (and matching
 * border/outline) of a tile at the given emphasis level. At High the body
 * is `main`, so the stripe steps to `dark` to stay visible against it.
 * Low and Medium keep the current behavior of `main` (which contrasts
 * against their paler bodies).
 */
export function getTileStripeShadeForEmphasis(
  palette: Pick<DataVisualizationColor, 'main' | 'dark'>,
  emphasis?: GraphVizTileEmphasis
): string {
  if (emphasis === GraphVizTileEmphasis.High) return palette.dark;
  return palette.main;
}

/**
 * Returns the fill color for the icon-variant tile's icon background shape.
 * Low (and legacy tiles with no emphasis set) keep the existing `main` shade
 * so default tiles look unchanged. Medium and High darken `main` by modest
 * amounts so the hue stays visible — `palette.dark` is a 60% darken of the
 * base color, which crushes saturated brand colors (e.g., green → near-navy)
 * and isn't suitable as a step for icon-tile emphasis.
 */
export function getIconBackgroundShadeForEmphasis(
  palette: Pick<DataVisualizationColor, 'main'>,
  emphasis?: GraphVizTileEmphasis
): string {
  if (emphasis === GraphVizTileEmphasis.High) return darken(palette.main, 0.2);
  if (emphasis === GraphVizTileEmphasis.Medium)
    return darken(palette.main, 0.15);
  return palette.main;
}

/**
 * SemanticTileKind provides a high-level categorization of what a Tile visually represents.
 *
 * It is used exclusively to decide which icon to render on an "icon variant" tile.
 * The enum is intentionally orthogonal to `TileVariant` (which controls color/state).
 *
 * NOTE: Adding a new kind?  • Provide a matching icon in `getIconForKind`.
 */
export { SemanticTileKind };

export function getIconForKind(
  kind?: SemanticTileKind
): ReactElement<IconProps> | undefined {
  if (!kind) return undefined;
  const kindToIconMap: Record<SemanticTileKind, ReactElement<IconProps>> = {
    [SemanticTileKind.Person]: <User01Icon />,
    [SemanticTileKind.House]: <Home03Icon />,
    [SemanticTileKind.Heart]: <HeartIcon />,
    [SemanticTileKind.Currency]: <CurrencyDollarCircleIcon />,
    [SemanticTileKind.Document]: <File02Icon />,
    [SemanticTileKind.Family]: <Family01Icon />,
    [SemanticTileKind.Charity]: <CoinsHandIcon />,
    [SemanticTileKind.Entity]: <Home03Icon />,
    [SemanticTileKind.Subtrust]: <Home03Icon />,
    [SemanticTileKind.Account]: <Home03Icon />,
    [SemanticTileKind.Organization]: <CoinsHandIcon />,
    [SemanticTileKind.Hidden]: <EyeOffIcon />,
  };
  return kindToIconMap[kind];
}

export function getIconForVariant(
  variant: TileVariant
): ReactElement<IconProps> {
  const variantToIconMap: Record<TileVariant, ReactElement<IconProps>> = {
    [TileVariant.Primary]: <Home03Icon />,
    [TileVariant.Secondary]: <CoinsHandIcon />,
    [TileVariant.Tertiary]: <CoinsHandIcon />,
    [TileVariant.Destructive]: <BankIcon />,
    [TileVariant.Default]: <User03Icon />,
    [TileVariant.Pixel]: <Home03Icon />,
    [TileVariant.External]: <ArrowUpIcon />,
  };
  return variantToIconMap[variant];
}

export function getOutOfEstateCategoryIcon(
  variant: TileVariant
): ReactElement<IconProps> | undefined {
  if (variant === TileVariant.Secondary) {
    const Icon = ESTATE_CATEGORY_ICON['out-of-estate-family'];
    return <Icon />;
  }
  if (variant === TileVariant.Tertiary) {
    const Icon = ESTATE_CATEGORY_ICON['out-of-estate-charity'];
    return <Icon />;
  }
  return undefined;
}

export function getTileDefaultWidth(isIconVariant: boolean) {
  if (isIconVariant) return undefined;
  return TILE_DEFAULT_WIDTH;
}

export interface SemanticTileKindNode {
  __typename?:
    | 'ClientProfile'
    | 'ClientOrganization'
    | 'Entity'
    | 'TestamentaryEntity'
    | 'Proposal';
  isCharitableOrganization?: boolean;
  /**
   * Entity kind of the node (only applicable when __typename === 'Entity').
   * This is used to further specialize the semantic kind for certain entity
   * sub-types such as donor-advised funds and private foundations.
   */
  entityKind?: EntityKind;
  inEstateStatus?: EntityInEstateStatus;
  testamentaryEntityKind?: TestamentaryEntityKind;
}

export function getIconForSemanticTileNode(
  node: SemanticTileKindNode
): ReactElement<IconProps> | undefined {
  if (node.inEstateStatus === EntityInEstateStatus.OutOfEstate) {
    if (isEstateCategoryCharitableNode(node)) {
      const Icon = ESTATE_CATEGORY_ICON['out-of-estate-charity'];
      return <Icon />;
    }

    const Icon = ESTATE_CATEGORY_ICON['out-of-estate-family'];
    return <Icon />;
  }

  if (
    node.__typename === 'ClientOrganization' &&
    node.isCharitableOrganization
  ) {
    const Icon = ESTATE_CATEGORY_ICON['out-of-estate-charity'];
    return <Icon />;
  }

  return getIconForKind(deriveSemanticTileKind(node));
}

function isEstateCategoryCharitableNode(node: SemanticTileKindNode): boolean {
  if (node.__typename === 'ClientOrganization') {
    return Boolean(node.isCharitableOrganization);
  }

  if (node.__typename === 'TestamentaryEntity') {
    return (
      node.testamentaryEntityKind === TestamentaryEntityKind.CharitableEntity ||
      node.testamentaryEntityKind === TestamentaryEntityKind.CharitableTrust
    );
  }

  return (
    node.__typename === 'Entity' &&
    node.entityKind !== undefined &&
    CHARITABLE_ENTITY_KINDS.includes(node.entityKind)
  );
}

export interface SemanticTileColors {
  /** Background fill for the icon swatch. */
  bg: string;
  /** Foreground (icon/text) color that contrasts the bg. */
  fg: string;
}

/**
 * Maps a SemanticTileKindNode to bg/fg colors used by tile/recipient swatches
 * across the DP and BDG forms.
 *
 * Two shade families are supported so both forms stay in sync when the
 * charitable / in-estate / out-of-estate rules change:
 *   - `solid`: dark fills with white text (DP form tile icons).
 *   - `subtle`: light tinted fills with darker text (BDG recipient card,
 *     and any UI that wants a softer treatment).
 */
export function getSemanticTileColors(
  node: SemanticTileKindNode | undefined,
  shade: 'solid' | 'subtle' = 'solid'
): SemanticTileColors {
  // Subtle (light) palette pairs the 100-tier background with the 600-tier
  // foreground so icon strokes stay legible on the tinted swatch. Mirrors
  // the priority order of the solid branch (InEstate > charitable >
  // OutOfEstate > fallback) so the two shade families stay aligned.
  if (shade === 'subtle') {
    if (!node) return { bg: COLORS.NAVY[100], fg: COLORS.NAVY[600] };
    if (node.inEstateStatus === EntityInEstateStatus.InEstate)
      return { bg: COLORS.NAVY[100], fg: COLORS.NAVY[600] };
    if (isCharitableNode(node))
      return { bg: COLORS.BLUE[100], fg: COLORS.BLUE[600] };
    if (node.inEstateStatus === EntityInEstateStatus.OutOfEstate)
      return { bg: COLORS.TEAL[100], fg: COLORS.TEAL[600] };
    return { bg: COLORS.NAVY[100], fg: COLORS.NAVY[600] };
  }

  // Solid (dark) palette: 500-tier background, white icon/text.
  if (!node) return { bg: COLORS.NAVY[500], fg: COLORS.PRIMITIVES.WHITE };
  if (node.inEstateStatus === EntityInEstateStatus.InEstate)
    return { bg: COLORS.NAVY[500], fg: COLORS.PRIMITIVES.WHITE };
  if (isCharitableNode(node))
    return { bg: COLORS.BLUE[500], fg: COLORS.PRIMITIVES.WHITE };
  if (node.inEstateStatus === EntityInEstateStatus.OutOfEstate)
    return { bg: COLORS.TEAL[500], fg: COLORS.PRIMITIVES.WHITE };
  return { bg: COLORS.NAVY[500], fg: COLORS.PRIMITIVES.WHITE };
}

function isCharitableNode(node: SemanticTileKindNode): boolean {
  const isCharitableOrg =
    node.__typename === 'ClientOrganization' && node.isCharitableOrganization;
  // Intentionally narrower than CHARITABLE_ENTITY_KINDS / getIsCharitableNode
  // in EstateWaterfall.utils: DP tile coloring has historically treated only
  // DAFs and private foundations (the non-trust charitable entities) as
  // "charitable" for icon-swatch color. CLT/CRT are charitable trusts and
  // continue to be styled as trusts here. Broadening this is a product/design
  // call, not a side-effect of the BDG refactor.
  const isCharitableEntity =
    node.__typename === 'Entity' &&
    (node.entityKind === EntityKind.DonorAdvisedFund ||
      node.entityKind === EntityKind.PrivateFoundation);
  return Boolean(isCharitableOrg || isCharitableEntity);
}

export function deriveSemanticTileKind(
  node: SemanticTileKindNode
): SemanticTileKind | undefined {
  if (!node.__typename) return undefined;
  switch (node.__typename) {
    case 'ClientProfile':
      return SemanticTileKind.Person;
    case 'ClientOrganization':
      return node.isCharitableOrganization
        ? SemanticTileKind.Organization
        : SemanticTileKind.Entity;
    case 'Entity':
      if (
        node.entityKind === EntityKind.DonorAdvisedFund ||
        node.entityKind === EntityKind.PrivateFoundation
      ) {
        return SemanticTileKind.Organization;
      }
      return SemanticTileKind.Entity;
    case 'TestamentaryEntity':
      return SemanticTileKind.Subtrust;
    default:
      return undefined;
  }
}

export interface TileSizeChangeResult {
  newWidth: number;
  newFontSizeVariant: TileFontSizeVariant;
}

/**
 * Cumulative tile width multiplier for a given font-size variant, counted
 * from the SM baseline. Mirrors the per-step scaling applied by
 * computeTileSizeChange so that load paths (applyNodeConfigurations,
 * generateDefaultState) can reconstruct the width a tile had when saved.
 */
export function tileWidthScaleForVariant(
  variant: TileFontSizeVariant | undefined
): number {
  if (!variant) return 1;
  const steps = ORDERED_TILE_FONT_SIZE_VARIANTS.indexOf(variant);
  if (steps <= 0) return 1;
  return Math.pow(TILE_WIDTH_ADJUSTMENT_INCREMENT, steps);
}

/**
 * Computes the new width and font size variant for a tile size change.
 * Returns null if the change would exceed the valid range.
 */
export function computeTileSizeChange(
  currentWidth: number,
  currentFontSizeVariant: TileFontSizeVariant | undefined,
  increment: boolean
): TileSizeChangeResult | null {
  const fontSizeVariant =
    currentFontSizeVariant ?? TILE_DEFAULT_FONT_SIZE_VARIANT;
  const currentIndex = ORDERED_TILE_FONT_SIZE_VARIANTS.indexOf(fontSizeVariant);

  const nextIndex = currentIndex + (increment ? 1 : -1);
  if (nextIndex < 0 || nextIndex >= ORDERED_TILE_FONT_SIZE_VARIANTS.length) {
    return null;
  }

  const widthAdjustment = increment
    ? TILE_WIDTH_ADJUSTMENT_INCREMENT
    : TILE_WIDTH_ADJUSTMENT_DECREMENT;

  const newFontSizeVariant = ORDERED_TILE_FONT_SIZE_VARIANTS[nextIndex]!;

  return {
    newWidth: currentWidth * widthAdjustment,
    newFontSizeVariant,
  };
}
